import { Box, ButtonBase, Stack, Typography } from '@mui/material';
import { useTranslation } from 'react-i18next';
import { theme } from '../../../../theme/ThemeProvider';
import { ConnectorStatus } from '../../../../common/enums/connectorStatus';
import { Connector } from '../../types';
import EvStationRoundedIcon from '@mui/icons-material/EvStationRounded';
import WorkspacePremiumRoundedIcon from '@mui/icons-material/WorkspacePremiumRounded';

interface ConnectorPickerProps {
  connectors: Connector[];
  onSelect: (connector: Connector) => void;
  stationPrice: number;
  /** Connectors here the driver is charging on — an account can hold both. */
  userActiveConnectorIds?: number[];
  premiumPricePerKwh?: number | null;
}

const getStatusColor = (status: string) => {
  switch (status) {
    case ConnectorStatus.Available:
      return theme.palette.green.main; // #24EDA4
    case ConnectorStatus.Charging:
      return theme.palette.blue.main; // #1DAEEF
    case ConnectorStatus.Preparing:
      return theme.palette.orange.main; // #EF9E1D
    default:
      return theme.palette.gray[50]; // #8A8A8E
  }
};

const getStatusBgAlpha = (status: string) => {
  const color = getStatusColor(status);
  return {
    bg: `${color}10`,
    bgHover: `${color}20`,
    border: `${color}40`,
  };
};

// A busy connector is a dead end unless the charge on it is the driver's own,
// in which case picking it reopens their charge.
const isSelectable = (c: Connector, userActiveConnectorIds: number[]) =>
  c.status === ConnectorStatus.Available ||
  c.status === ConnectorStatus.Preparing ||
  (c.status === ConnectorStatus.Charging &&
    userActiveConnectorIds.includes(c.connectorId));

export const ConnectorPicker: React.FC<ConnectorPickerProps> = ({
  connectors,
  onSelect,
  stationPrice,
  userActiveConnectorIds = [],
  premiumPricePerKwh,
}) => {
  const { t } = useTranslation();
  const hasPremiumPrice = typeof premiumPricePerKwh === 'number';

  const premiumColor = theme.palette.orange.main;

  return (
    <Stack alignItems="center" sx={{ width: '100%', gap: '20px' }}>
      <Stack alignItems="center" sx={{ gap: '10px' }}>
        <Typography variant="h2" color="common.white" textAlign="center">
          {t('chargeStationPage.chooseConnector')}
        </Typography>
        {hasPremiumPrice ? (
          <Stack
            alignItems="center"
            sx={{
              gap: '4px',
              padding: '8px 14px',
              borderRadius: '12px',
              border: `1px solid ${premiumColor}55`,
              background: `linear-gradient(135deg, ${premiumColor}1F 0%, ${premiumColor}08 100%)`,
            }}
          >
            <Stack direction="row" alignItems="center" sx={{ gap: '6px' }}>
              <WorkspacePremiumRoundedIcon
                sx={{ fontSize: 16, color: premiumColor }}
              />
              <Typography
                variant="body2"
                sx={{
                  color: premiumColor,
                  fontWeight: 700,
                  textTransform: 'uppercase',
                  letterSpacing: '0.06em',
                  fontSize: '11px',
                  lineHeight: 1,
                }}
              >
                {t('orderPage.yourPremiumPrice')}
              </Typography>
            </Stack>
            <Typography
              variant="body1"
              color="common.white"
              sx={{ fontWeight: 700, lineHeight: 1.2 }}
            >
              {premiumPricePerKwh} ₴/{t('orderPage.energyUnit')}
            </Typography>
            <Typography
              variant="body2"
              sx={{
                color: theme.palette.gray[50],
                textDecoration: 'line-through',
                fontSize: '12px',
                lineHeight: 1,
              }}
            >
              {stationPrice} ₴/{t('orderPage.energyUnit')}
            </Typography>
          </Stack>
        ) : (
          <Typography
            variant="body2"
            color="common.white"
            textAlign="center"
            sx={{ opacity: 0.5 }}
          >
            {stationPrice} ₴/{t('orderPage.energyUnit')}
          </Typography>
        )}
      </Stack>

      <Stack sx={{ width: '100%', gap: '12px' }}>
        {connectors.map((connector) => {
          const selectable = isSelectable(connector, userActiveConnectorIds);
          const statusColor = getStatusColor(connector.status as string);
          const { bg, bgHover, border } = getStatusBgAlpha(connector.status as string);

          return (
            <ButtonBase
              key={connector.connectorId}
              disabled={!selectable}
              onClick={() => onSelect(connector)}
              sx={{
                width: '100%',
                borderRadius: '15px',
                border: `1px solid ${border}`,
                padding: '16px',
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                opacity: selectable ? 1 : 0.5,
                transition: 'transform 0.12s ease, background-color 0.15s ease',
                backgroundColor: bg,
                WebkitTapHighlightColor: 'transparent',
                touchAction: 'manipulation',
                '@media (hover: hover)': {
                  '&:hover': selectable ? { backgroundColor: bgHover } : {},
                },
                '&:active': selectable
                  ? { transform: 'scale(0.97)', backgroundColor: bgHover }
                  : {},
                '&.Mui-disabled': {
                  opacity: 0.5,
                },
              }}
            >
              <Stack direction="row" alignItems="center" sx={{ gap: '12px' }}>
                <Box
                  sx={{
                    width: 40,
                    height: 40,
                    borderRadius: '10px',
                    backgroundColor: `${statusColor}25`,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                  }}
                >
                  <EvStationRoundedIcon
                    sx={{
                      fontSize: 22,
                      color: statusColor,
                    }}
                  />
                </Box>
                <Typography
                  variant="body1"
                  color="common.white"
                  sx={{ fontWeight: 500 }}
                >
                  {t('connectorCard.connector')} {connector.connectorId}
                </Typography>
              </Stack>

              <Box
                sx={{
                  padding: '4px 12px',
                  borderRadius: '6px',
                  backgroundColor: `${statusColor}18`,
                  border: `1px solid ${statusColor}50`,
                }}
              >
                <Typography variant="body2" sx={{ fontWeight: 600, color: statusColor }}>
                  {t(`statuses.connector.${connector.status}`)}
                </Typography>
              </Box>
            </ButtonBase>
          );
        })}
      </Stack>
    </Stack>
  );
};
